import { setLocalStorage } from "../core/localStorage/localStorage";
import { clearToken } from "./token-store";
import { revokeAndLogout } from "./logout";

/**
 * IIDM-3064 T3 — the portal is a PUBLIC OAuth client: authorization code + PKCE (S256) + `state`.
 *
 * Before: the authorize redirect carried only client_id / redirect_uri / response_type, and the
 * token exchange sent the client secret from a REACT_APP_ env var — a secret compiled
 * into a JavaScript bundle every visitor downloads, i.e. not a secret. There was no `state`, so a
 * callback URL carrying an attacker's `code` was exchanged and logged the victim into the
 * attacker's account (login CSRF).
 *
 * Contract (IIDM-3063, sso): the portal's `oauth_clients` row has no secret, which makes
 * `code_challenge` mandatory at /oauth/authorize and `code_verifier` mandatory at /oauth/token.
 * The SSO's own login hop preserves `code_challenge`, `code_challenge_method`, `state` and
 * `scope`. NO refresh token is issued to a public client (refresh grant → 400 invalid_grant), so
 * on a 401 the app re-runs this authorize; the SSO session answers it without a prompt.
 *
 * The client id comes from `REACT_APP_CLIENT_ID` (per environment, never hard-coded).
 */
export const VERIFIER_KEY = "pkce_verifier";
export const STATE_KEY = "oauth_state";
/** When the last 401-driven re-authorize started (ms) — the redirect-loop breaker. */
export const REAUTH_AT_KEY = "oauth_reauth_at";
/** A second 401 within this window of a re-authorize means a fresh token is ALSO refused. */
export const REAUTH_WINDOW_MS = 60000;

/** RFC 4648 §5 base64url, no padding. */
export function base64url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** @return {string} base64url of `n` CSPRNG bytes */
export function randomToken(n = 32): string {
  const bytes = new Uint8Array(n);
  window.crypto.getRandomValues(bytes);

  return base64url(bytes);
}

/**
 * S256 challenge for a verifier. Deliberately NO fallback to `plain`: `crypto.subtle` exists only
 * in a secure context (https or localhost), and a missing one must fail loudly, not downgrade.
 */
export async function s256(verifier: string): Promise<string> {
  const subtle = window.crypto && window.crypto.subtle;
  if (!subtle) {
    throw new Error("PKCE needs crypto.subtle (https or localhost)");
  }
  const digest = await subtle.digest("SHA-256", new TextEncoder().encode(verifier));

  return base64url(new Uint8Array(digest));
}

/** @return {Promise<{verifier: string, challenge: string}>} a fresh PKCE pair (43-char verifier) */
export async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomToken(32);

  return { verifier, challenge: await s256(verifier) };
}

function session(): Storage | null {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

function clearPkce(): void {
  const s = session();
  if (s) {
    s.removeItem(VERIFIER_KEY);
    s.removeItem(STATE_KEY);
  }
}

/**
 * Builds the authorize URL. Pure — no storage, no navigation.
 */
export function buildAuthorizeUrl(state: string, challenge: string): string {
  const params = new URLSearchParams({
    client_id: `${process.env.REACT_APP_CLIENT_ID || ""}`,
    redirect_uri: `${process.env.REACT_APP_REDIRECT_URI || ""}`,
    response_type: "code",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  return `${process.env.REACT_APP_AUTHORIZATION_URL || ""}?${params.toString()}`;
}

/**
 * Starts a PKCE authorize: new verifier + state into sessionStorage (same tab survives the SSO
 * round trip), then navigate. `smartLink`, when given and not "/", is remembered in the existing
 * `payload` localStorage key that `permission-api-component` reads after login (unchanged shape).
 */
export async function startAuthorize(smartLink?: string): Promise<void> {
  const { verifier, challenge } = await createPkcePair();
  const state = randomToken(32);
  const s = session();
  if (s) {
    s.setItem(VERIFIER_KEY, verifier);
    s.setItem(STATE_KEY, state);
  }
  if (smartLink && smartLink !== "/") {
    setLocalStorage(
      "payload",
      JSON.stringify({
        client_id: `${process.env.REACT_APP_CLIENT_ID || ""}`,
        redirect_uri: `${process.env.REACT_APP_REDIRECT_URI || ""}`,
        response_type: "code",
        smart_link: smartLink,
      })
    );
  }
  window.location.href = buildAuthorizeUrl(state, challenge);
}

/**
 * Validates an SSO callback. The stored verifier/state are SINGLE USE: removed here whatever the
 * outcome, so replaying the same callback URL (the post-login reload keeps `?code=&state=`) is
 * refused and falls through to a fresh authorize.
 *
 * @return {{code: string, verifier: string} | null} null = refuse (no state, mismatch, no verifier)
 */
export function consumeCallback(search: string): { code: string; verifier: string } | null {
  const params = new URLSearchParams(search);
  const code = params.get("code") || "";
  const returned = params.get("state") || "";
  const s = session();
  const expected = (s && s.getItem(STATE_KEY)) || "";
  const verifier = (s && s.getItem(VERIFIER_KEY)) || "";
  clearPkce();
  if (!code || !returned || !expected || returned !== expected || !verifier) {
    return null;
  }

  return { code, verifier };
}

/**
 * Exchanges the code. JSON body as the portal has always sent (IIDM-3063 contract item 3),
 * with `code_verifier` and WITHOUT any client secret.
 *
 * @return {Promise<string>} the access token, or "" on any failure
 */
export async function exchangeCode(code: string, verifier: string): Promise<string> {
  const body = {
    grant_type: "authorization_code",
    client_id: `${process.env.REACT_APP_CLIENT_ID || ""}`,
    redirect_uri: `${process.env.REACT_APP_REDIRECT_URI || ""}`,
    code,
    code_verifier: verifier,
  };
  try {
    const res = await fetch(`${process.env.REACT_APP_SERVER_URL || ""}/token`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "content-type": "application/json" },
    });
    const json = await res.json();

    return json && typeof json.access_token === "string" ? json.access_token : "";
  } catch (e) {
    return "";
  }
}

let reauthInFlight: Promise<void> | null = null;

/**
 * The 401 path. There is no refresh token (IIDM-3063 T5), so: drop the dead token and re-run the
 * authorize — transparent while the SSO session lives, the login page once it does not.
 *
 * Loop breaker: if a re-authorize already started within REAUTH_WINDOW_MS, a fresh token is being
 * refused too (a BE-side gate, not expiry); re-authorizing again would spin forever, so fall back
 * to the full revoke + SSO logout, which ends the SSO session and stops the loop.
 * Concurrent 401s share one run, so only one state/verifier pair is ever written.
 */
export function reauthorizeOn401(): Promise<void> {
  if (reauthInFlight) {
    return reauthInFlight;
  }
  reauthInFlight = (async () => {
    const s = session();
    const last = Number((s && s.getItem(REAUTH_AT_KEY)) || 0);
    if (last && Date.now() - last < REAUTH_WINDOW_MS) {
      if (s) {
        s.removeItem(REAUTH_AT_KEY);
      }
      await revokeAndLogout();

      return;
    }
    if (s) {
      s.setItem(REAUTH_AT_KEY, String(Date.now()));
    }
    clearToken();
    try {
      await startAuthorize(window.location.pathname);
    } catch (e) {
      await revokeAndLogout();
    }
  })().finally(() => {
    reauthInFlight = null;
  });

  return reauthInFlight;
}
